/**
 * Auth-time dashboard prefetch (Paul's cold-start ruling, 2026-07-15).
 *
 * MEASURED CONTEXT: on landing, the b2c dashboard fires ~19 API calls fully
 * in parallel — no frontend waterfall. Warm, placeholders clear in
 * ~600-850ms; the bad 3-second-plus experience happens on COLD function apps
 * (Y1 scale-to-zero). Login itself talks only to Supabase (Frankfurt), so
 * while a user spends ~10s on the password screen + the MFA code screen, our
 * APIM/Functions stay cold and the user pays the cold start AFTER they think
 * they've signed in.
 *
 * `warmBackend()` (warmup.ts) already pings /health from the auth screens to
 * spin up the gateway + FA. This module goes one step further for the b2c
 * dashboard path: fire-and-forget React Query prefetches of the dashboard's
 * PRIMARY queries — the same query keys + queryFns the dashboard components
 * use (imported from the modules that define the hooks, so they cannot
 * drift) — filling the cache while the user types their MFA code. Landing
 * then paints from cache instantly, and the queries the prefetch didn't
 * cover hit a by-then-warm backend.
 *
 * What gets prefetched (mirrors the dashboard's above-the-fold reads):
 *   - /v1/dashboard/summary            (hero headline)       — ['dashboard','summary']
 *   - /v1/trusts                       (trust list)          — ['trusts','list']
 *   - /v1/estate/assets                (estate card + split) — ['estate','assets']
 *   then, chained on the trusts list resolving, for the FIRST trust (the
 *   dashboard's primary trust — same `trusts[0]` the dashboard/hero use):
 *   - /v1/app/dashboard/holdings       — ['dashboard','holdings', id]
 *   - /v1/trusts/{id}/aum-snapshots?limit=24 — ['aum-snapshots','list', id, 24]
 *     (limit 24 matches the hero's `useAumSnapshots(id, { limit: 24 })`)
 *   - /v1/trusts/{id}/assets           — ['trusts','assets', id]
 *     (wealth-distribution + registration-journey)
 *
 * GUARD RAILS (per the ruling):
 *   - fire-and-forget: failures are silent, nothing here can block or slow
 *     the auth flow (prefetchQuery already swallows errors; the trusts-list
 *     chain catches its own);
 *   - callers only invoke this when a session exists AND the post-auth
 *     landing surface is the b2c dashboard (`landingRouteForRole(role) ===
 *     '/app/dashboard'`) — firm staff / auditors / superadmins land on
 *     different surfaces and are not prefetched for;
 *   - React Query dedupes: a second call (login fired one, mfa-verify fires
 *     another) no-ops within the 30s staleTime.
 */

import type { QueryClient } from '@tanstack/react-query';
import {
  DASHBOARD_SUMMARY_QUERY_KEY,
  fetchDashboardSummary,
  dashboardHoldingsQueryKey,
  fetchDashboardHoldings,
} from './dashboard';
import {
  TRUSTS_LIST_QUERY_KEY,
  fetchTrustsList,
  trustAssetsQueryKey,
  fetchTrustAssets,
} from './trusts';
import { ESTATE_ASSETS_QUERY_KEY, fetchEstateAssets } from './estate';
import { aumSnapshotsQueryKey, fetchAumSnapshots } from './aum-snapshots';

/** The hero requests 24 monthly snapshot marks — the prefetch key must carry
 *  the SAME limit or the cache entry would miss. */
const HERO_AUM_SNAPSHOT_LIMIT = 24;

/**
 * Fire-and-forget prefetch of the b2c dashboard's primary queries. Never
 * throws, never rejects — safe to call inline in the auth flow.
 */
export function prefetchDashboard(client: QueryClient): void {
  try {
    // Independent tenant-level reads — fire all three immediately.
    void client.prefetchQuery({
      queryKey: DASHBOARD_SUMMARY_QUERY_KEY,
      queryFn: ({ signal }) => fetchDashboardSummary(signal),
    });
    void client.prefetchQuery({
      queryKey: ESTATE_ASSETS_QUERY_KEY,
      queryFn: ({ signal }) => fetchEstateAssets(signal),
    });

    // The trusts list, then the first trust's per-trust reads chained on it
    // (the ids aren't known until the list resolves). fetchQuery caches under
    // the same key prefetchQuery would, AND hands back the data for chaining.
    void client
      .fetchQuery({
        queryKey: TRUSTS_LIST_QUERY_KEY,
        queryFn: ({ signal }) => fetchTrustsList(signal),
      })
      .then((res) => {
        const firstTrust = res.trusts[0];
        if (!firstTrust) return;
        void client.prefetchQuery({
          queryKey: dashboardHoldingsQueryKey(firstTrust.id),
          queryFn: ({ signal }) => fetchDashboardHoldings(firstTrust.id, signal),
        });
        void client.prefetchQuery({
          queryKey: aumSnapshotsQueryKey(firstTrust.id, HERO_AUM_SNAPSHOT_LIMIT),
          queryFn: ({ signal }) =>
            fetchAumSnapshots(firstTrust.id, HERO_AUM_SNAPSHOT_LIMIT, signal),
        });
        void client.prefetchQuery({
          queryKey: trustAssetsQueryKey(firstTrust.id),
          queryFn: ({ signal }) => fetchTrustAssets(firstTrust.id, signal),
        });
      })
      .catch(() => {
        // Silent by design — a failed prefetch just means the dashboard
        // fetches normally on landing (against a by-then-warm backend).
      });
  } catch {
    // Defensive: nothing in the prefetch path may ever disturb auth.
  }
}
